import { ChangeEvent, useRef, useTransition } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { AddNoCircle, Close } from '@mint-lib/icons';
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerOverlay,
  DrawerPortal,
  DrawerRoot,
  DrawerTitle,
} from '@mint-lib/tw-components';
import { SearchBar } from '@mint-lib/tw-components/patterns';

import { useCustomMetricsDrawer } from '../../contexts/CustomMetricsContext.jsx';
import { EmptyState } from '../atoms/EmptyState.jsx';
import { LimitProgressBar } from '../atoms/LimitProgressBar.jsx';
import { Tooltip } from '../atoms/Tooltip.jsx';
import { CustomMetricForm } from '../molecules/CustomMetricForm.jsx';
import { CustomMetricsEditorList } from '../molecules/CustomMetricsEditorList.jsx';

export const CustomMetricsDrawer = () => {
  const { t } = useTranslation('@myn/import-hub');
  const {
    isOpen,
    closeDrawer,
    isLoading,
    searchValue,
    searchMetrics,
    customMetrics,
    metricsMaxCount,
    metricsTotalCount,
    canMetricBeCreated,
    selectedMetricDraft,
    openMetricForCreate,
    isAddMetricMode,
    onDrawerAnimationEnd,
  } = useCustomMetricsDrawer();

  const [, startTransition] = useTransition();
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event.currentTarget.value;
    startTransition(() => {
      searchMetrics(newValue);
    });
  };

  const handleSearchReset = () => {
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
      searchMetrics('');
    }
  };

  const notFound = !isLoading && Boolean(searchValue) && !customMetrics.length;
  const isAddingDisabled =
    Boolean(searchValue) || isAddMetricMode || !canMetricBeCreated;

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          closeDrawer();
        }
      }}
    >
      <DrawerPortal>
        <DrawerOverlay />
        <DrawerRoot
          onAnimationEnd={onDrawerAnimationEnd}
          size="75%"
          className="max-w-[1300px] px-4 pt-4 pb-2"
          data-testid="custom-metrics-drawer"
        >
          <DrawerContent className="flex flex-1 flex-col overflow-hidden p-2">
            <div className="mb-4 flex items-center">
              <Button
                aria-label={t('Close')}
                data-dd-action-name="Close"
                onClick={closeDrawer}
                icon
                variant="plain"
                size="small"
                className="mr-2"
              >
                <Close />
              </Button>
              <DrawerTitle>{t('Custom metrics')}</DrawerTitle>
              <DrawerDescription className="sr-only">
                {t('Search, add, or edit custom metrics.')}
              </DrawerDescription>
            </div>
            <div className="flex min-h-0 gap-4">
              <div className="flex h-full w-[422px] flex-col">
                <div className="mb-4">
                  <div className="mb-4 flex items-center gap-2">
                    <Tooltip
                      description={t(
                        'You have reached the maximum number of custom metrics',
                      )}
                      disabled={canMetricBeCreated}
                      asChild
                    >
                      <div className="grow">
                        <Button
                          size="large"
                          variant="contained"
                          color="primary"
                          onClick={openMetricForCreate}
                          disabled={isAddingDisabled}
                          className="w-full"
                          data-dd-action-name="Add New Custom Metric"
                        >
                          <AddNoCircle className="size-4" />
                          {t('Add New Custom Metric')}
                        </Button>
                      </div>
                    </Tooltip>
                    {metricsMaxCount ? (
                      <LimitProgressBar
                        value={metricsTotalCount}
                        total={metricsMaxCount}
                        tooltip={t(
                          'The number of custom metrics that are currently available for addition',
                        )}
                      />
                    ) : null}
                  </div>
                  <SearchBar
                    ref={searchInputRef}
                    defaultValue={searchValue}
                    onChange={handleSearchChange}
                    onClear={handleSearchReset}
                    disabled={metricsTotalCount === 0}
                    placeholder={t('Search')}
                    className="w-full"
                  />
                </div>
                {!notFound && <CustomMetricsEditorList />}
              </div>
              {!notFound && <CustomMetricForm key={selectedMetricDraft.id} />}
            </div>
            {notFound && (
              <EmptyState
                icon="IllustrationNoResults"
                text={t('No results found for "{{searchValue}}"', {
                  searchValue,
                })}
                description={t(
                  'Try adjusting your search in order to find what you are looking for.',
                )}
                className="shadow-none"
              />
            )}
          </DrawerContent>
        </DrawerRoot>
      </DrawerPortal>
    </Drawer>
  );
};
